import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

/**
 * Apps → Optimizer (xcons-docs#524). La pantalla no conoce los campos de Lepton: los dibuja desde
 * el catálogo que devuelve la API (`fields`), así que un campo nuevo del manual no toca el front.
 */

export type OptimizerFieldValue = number | boolean | string;

export interface OptimizerField {
  key: string;
  group: string;
  type: "number" | "integer" | "boolean" | "string" | "enum" | "mask";
  label: string;
  help?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  options?: { value: number | string; label: string }[];
  /** El valor efectivo: el del seller o el default. `null` si no viaja a Lepton. */
  value: OptimizerFieldValue | null;
  default: OptimizerFieldValue | null;
  isDefault: boolean;
}

export interface SellerOptimizerConfig {
  sellerCode: string;
  hasLeptonAccount: boolean;
  /** Entero: la pantalla lo muestra tal cual, para que se vea qué está cargado. */
  leptonUserId: string | null;
  fields: OptimizerField[];
}

export interface UpdateSellerOptimizerConfig {
  /** `""` lo borra. Ausente, no se toca. */
  leptonUserId?: string | null;
  /** `null` vuelve el campo a su default. */
  values?: Record<string, OptimizerFieldValue | null>;
}

export interface StoreOptimizerConfig {
  storeCode: string;
  offerBoardOptimizations: boolean;
  sellers: {
    sellerCode: string;
    name: string | null;
    hasLeptonAccount: boolean;
  }[];
}

const sellerUrl = (sellerCode: string) =>
  `${API_BASE_URL}/sellers/${encodeURIComponent(sellerCode)}/optimizer-config`;

const storeUrl = (storeCode: string) =>
  `${API_BASE_URL}/stores/${encodeURIComponent(storeCode)}/optimizer-config`;

export async function getSellerOptimizerConfig(
  sellerCode: string,
): Promise<SellerOptimizerConfig> {
  const { data } = await axiosInstance.get<SellerOptimizerConfig>(
    sellerUrl(sellerCode),
  );
  return data;
}

export async function updateSellerOptimizerConfig(
  sellerCode: string,
  payload: UpdateSellerOptimizerConfig,
): Promise<SellerOptimizerConfig> {
  const { data } = await axiosInstance.put<SellerOptimizerConfig>(
    sellerUrl(sellerCode),
    payload,
  );
  return data;
}

export async function getStoreOptimizerConfig(
  storeCode: string,
): Promise<StoreOptimizerConfig> {
  const { data } = await axiosInstance.get<StoreOptimizerConfig>(
    storeUrl(storeCode),
  );
  return data;
}

export async function updateStoreOptimizerConfig(
  storeCode: string,
  offerBoardOptimizations: boolean,
): Promise<StoreOptimizerConfig> {
  const { data } = await axiosInstance.put<StoreOptimizerConfig>(
    storeUrl(storeCode),
    {
      offerBoardOptimizations,
    },
  );
  return data;
}

/**
 * Las opciones de una máscara que suman `value`. Las máscaras de Lepton llegan a 2³¹ y los
 * operadores de bits de JS trabajan en 32 bits con signo: se usa `BigInt`.
 */
export function maskToValues(
  field: OptimizerField,
  value: number | null,
): number[] {
  if (value === null) return [];
  const v = BigInt(value);
  return (field.options ?? [])
    .map((o) => Number(o.value))
    .filter((bit) => (v & BigInt(bit)) !== BigInt(0));
}

export function valuesToMask(bits: number[]): number {
  return Number(bits.reduce((acc, bit) => acc | BigInt(bit), BigInt(0)));
}
